<script setup lang="ts">
import { ref, watch } from 'vue';
import ConnxTransition from '@/components/Atoms/ConnxTransition.vue';
import ConnxSecurityCodeInput from '@/components/Organisms/ConnxSecurityCodeInput.vue';
import ConnxAlert from '@/components/Molecules/ConnxAlert.vue';
import useTFAuthenticationSettingsStore from '@/stores/tf-authentication-settings';

const emit = defineEmits(['complete']);
const authSettingsStore = useTFAuthenticationSettingsStore();
const validatingCode = ref(false);
const securityInput = ref();
const trustDevice = ref(false);
const showErrorMessage = ref(false);

const updateKey = (code : string | null) => {
  authSettingsStore.updateSecretKey(code);
};

const clearErrorMessage = () => {
  showErrorMessage.value = false;
};

const showError = () => {
  showErrorMessage.value = true;
  securityInput.value.clearCodes();
  authSettingsStore.updateSecretKey(null);
};

const validate = () => {
  emit('complete');
};

defineExpose({
  showError,
});

watch(trustDevice, (value : boolean) => {
  authSettingsStore.updateCanTrustThisDevice(value);
});

</script>

<template>
  <div class="tw-text-center">
    <h2 class="tw-text-2xl tw-font-semibold tw-mb-8">
      Verify your code
    </h2>
    <p class="tw-mb-8">
      Enter the 6-digit verification code generated by your authenticator app.
    </p>

    <div v-show="!validatingCode">
      <connx-security-code-input
        ref="securityInput"
        @click="clearErrorMessage()"
        @key-updated="(code) => updateKey(code)"
        @validate="() => validate()"
      />
      <div
        v-if="authSettingsStore.settings.isTrustedDevicesAllowed"
        class="tw-flex tw-items-center tw-mt-10 tw-justify-center"
      >
        <label
          class="tw-text-gray-900 tw-flex tw-items-center tw-space-x-2"
        >
          <input
            v-model="trustDevice"
            type="checkbox"
            class="
              tw-w-4
              tw-h-4
              tw-text-connx-primary
              tw-bg-connx-grey-tint-90
              tw-rounded
              tw-border-connx-grey-tint-65
              tw-border-solid
              focus:tw-ring-2
            "
          >
          <span>Trust this device for {{ authSettingsStore.settings.desktopTrustedDays }} days</span>
        </label>
      </div>
      <connx-transition>
        <connx-alert
          v-show="showErrorMessage"
          type="error"
          class="tw-mt-5"
        >
          Invalid passcode. Please try again.
        </connx-alert>
      </connx-transition>
    </div>
  </div>
</template>
